import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import { mealTargets, totalTarget, type MealRecord } from "@/mocks/records";
import { categoryColors, foodCategories, type FoodNutrition } from "@/mocks/fooddb";
import { findFood, scaleNutrition, nutritionToPer100g } from "@/utils/foodParser";
import { useRecords } from "@/hooks/useRecords";
import { deleteRecord, updateRecord } from "@/store/recordsStore";

const mealLabels: Record<string, { label: string; icon: string; bg: string; text: string }> = {
  breakfast: { label: "早餐", icon: "ri-sun-line", bg: "bg-mint-50", text: "text-mint-500" },
  lunch: { label: "午餐", icon: "ri-restaurant-line", bg: "bg-amber-50", text: "text-amber-500" },
  dinner: { label: "晚餐", icon: "ri-moon-clear-line", bg: "bg-indigo-50", text: "text-indigo-500" },
};

interface EditState {
  id: string;
  mealType: "breakfast" | "lunch" | "dinner";
  name: string;
  category: string;
  quantity: number;
  unit: string;
  weight: number;
  foodId: string;
  per100g: FoodNutrition;
}

export default function RecordsPage() {
  const navigate = useNavigate();
  const records = useRecords();
  const [editing, setEditing] = useState<EditState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 1500);
  };

  const grouped = useMemo(() => {
    const map: Record<string, MealRecord[]> = { breakfast: [], lunch: [], dinner: [] };
    records.forEach((r) => {
      if (map[r.mealType]) map[r.mealType].push(r);
    });
    return map;
  }, [records]);

  const totalIntake = useMemo(() => records.reduce((s, r) => s + r.calories, 0), [records]);
  const progress = Math.min(100, Math.round((totalIntake / totalTarget) * 100));

  const startEdit = (r: MealRecord) => {
    const food = findFood(r.name);
    const per100g = food
      ? food.per100g
      : nutritionToPer100g(
          { calories: r.calories, protein: r.protein, carbs: r.carbs, fat: r.fat, fiber: r.fiber, vitC: r.vitC },
          r.weight
        );
    setEditing({
      id: r.id,
      mealType: r.mealType,
      name: r.name,
      category: r.category,
      quantity: r.quantity,
      unit: r.unit,
      weight: r.weight,
      foodId: food?.id ?? r.id,
      per100g,
    });
  };

  const preview = editing ? scaleNutrition(editing.per100g, editing.weight) : null;

  const handleName = (v: string) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const food = findFood(v);
      if (food && food.id !== prev.foodId) {
        return {
          ...prev,
          name: v,
          foodId: food.id,
          category: food.category,
          per100g: food.per100g,
          unit: food.defaultUnit,
          weight: Math.max(1, Math.round(food.unitWeight * prev.quantity)),
        };
      }
      return { ...prev, name: v };
    });
  };

  const handleQuantity = (v: number) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const q = Math.max(0, v);
      const food = findFood(prev.name);
      const weight = food ? Math.max(1, Math.round(food.unitWeight * q)) : prev.weight;
      return { ...prev, quantity: q, weight };
    });
  };

  const handleWeight = (v: number) => setEditing((prev) => (prev ? { ...prev, weight: v } : prev));
  const handleCategory = (c: string) => setEditing((prev) => (prev ? { ...prev, category: c } : prev));
  const handleMealType = (m: "breakfast" | "lunch" | "dinner") =>
    setEditing((prev) => (prev ? { ...prev, mealType: m } : prev));

  const saveEdit = () => {
    if (!editing) return;
    if (editing.weight <= 0) {
      showToast("重量必须大于 0");
      return;
    }
    const n = scaleNutrition(editing.per100g, editing.weight);
    updateRecord(editing.id, {
      mealType: editing.mealType,
      name: editing.name,
      category: editing.category,
      quantity: editing.quantity,
      unit: editing.unit,
      weight: editing.weight,
      calories: n.calories,
      protein: n.protein,
      carbs: n.carbs,
      fat: n.fat,
      fiber: n.fiber,
      vitC: n.vitC,
    });
    setEditing(null);
    showToast("已更新记录");
  };

  return (
    <AppShell>
      {/* Header */}
      <header className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-warm-200 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-gray-600 text-lg"></i>
        </button>
        <h1 className="text-base font-semibold text-gray-900">饮食记录</h1>
      </header>

      {/* Total summary */}
      <section className="px-5 mt-1">
        <div className="bg-white rounded-card p-4 border border-warm-200">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs text-gray-400">今日摄入</p>
              <p className="text-2xl font-semibold text-gray-900 mt-0.5">
                {totalIntake}
                <span className="text-sm text-gray-400 font-normal"> / {totalTarget} kcal</span>
              </p>
            </div>
            <span className="text-xs font-medium text-mint-500 bg-mint-50 px-2 py-1 rounded-md">
              {progress}%
            </span>
          </div>
          <div className="mt-3 h-2 bg-warm-100 rounded-full overflow-hidden">
            <div className="h-full bg-mint-400 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      </section>

      {/* Meal groups */}
      <section className="px-5 mt-4 pb-8">
        {Object.entries(grouped).map(([key, list]) => {
          const info = mealLabels[key];
          const groupCalories = list.reduce((s, r) => s + r.calories, 0);
          const target = mealTargets[key as keyof typeof mealTargets];
          const gp = Math.min(100, Math.round((groupCalories / target) * 100));
          return (
            <div key={key} className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-full ${info.bg} flex items-center justify-center ${info.text}`}>
                    <i className={`${info.icon} text-sm`}></i>
                  </span>
                  <span className="text-sm font-medium text-gray-900">{info.label}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {groupCalories} / {target} kcal
                </span>
              </div>
              <div className="h-1.5 bg-warm-100 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-mint-400 rounded-full transition-all" style={{ width: `${gp}%` }}></div>
              </div>

              {list.length === 0 ? (
                <div className="text-center py-4 text-xs text-gray-400 bg-white rounded-card border border-warm-200">
                  还没有记录，去首页添加一下吧
                </div>
              ) : (
                <div className="space-y-2">
                  {list.map((r) => (
                    <div
                      key={r.id}
                      className="bg-white rounded-card p-3 border border-warm-200 flex items-center justify-between"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.name}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${categoryColors[r.category] ?? categoryColors["其他"]}`}>
                            {r.category}
                          </span>
                          <span className="text-[11px] text-gray-400 shrink-0">{r.time}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-400">
                            {r.quantity}{r.unit} · {r.weight} g
                          </span>
                          <span className="text-xs text-gray-400">
                            P{r.protein} C{r.carbs} F{r.fat}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {r.calories}
                          <span className="text-[11px] text-gray-400 font-normal"> kcal</span>
                        </span>
                        <button
                          onClick={() => startEdit(r)}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-mint-500 cursor-pointer transition-colors"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => {
                            deleteRecord(r.id);
                            showToast("已删除记录");
                          }}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-red-400 cursor-pointer transition-colors"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditing(null)}></div>
          <div className="relative w-full max-w-md bg-white rounded-t-2xl p-5 pb-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-base font-semibold text-gray-900">编辑记录</p>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 rounded-full bg-warm-50 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">餐次</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["breakfast", "lunch", "dinner"] as const).map((k) => (
                    <button
                      key={k}
                      onClick={() => handleMealType(k)}
                      className={`h-10 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-colors ${
                        editing.mealType === k
                          ? "bg-mint-400 text-white font-medium"
                          : "bg-warm-50 border border-warm-200 text-gray-600"
                      }`}
                    >
                      {mealLabels[k].label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 block mb-1">食物名称</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => handleName(e.target.value)}
                  className="w-full h-11 rounded-lg bg-warm-50 border border-warm-200 px-3 text-sm text-gray-800 outline-none focus:border-mint-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">类别</label>
                  <select
                    value={editing.category}
                    onChange={(e) => handleCategory(e.target.value)}
                    className="w-full h-11 rounded-lg bg-warm-50 border border-warm-200 px-3 text-sm text-gray-800 outline-none focus:border-mint-400 cursor-pointer"
                  >
                    {foodCategories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">数量</label>
                  <div className="flex items-center gap-1 h-11 rounded-lg bg-warm-50 border border-warm-200 px-3">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={editing.quantity}
                      onChange={(e) => handleQuantity(Number(e.target.value))}
                      className="w-full text-sm text-gray-800 outline-none bg-transparent text-right"
                    />
                    <span className="text-xs text-gray-400">{editing.unit}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 block mb-1">重量（克）</label>
                <div className="flex items-center gap-1 h-11 rounded-lg bg-warm-50 border border-warm-200 px-3">
                  <input
                    type="number"
                    min="0"
                    value={editing.weight}
                    onChange={(e) => handleWeight(Number(e.target.value))}
                    className="w-full text-sm text-gray-800 outline-none bg-transparent text-right"
                  />
                  <span className="text-xs text-gray-400">g</span>
                </div>
              </div>

              {preview && (
                <div className="rounded-lg bg-mint-50 p-3 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-gray-400">热量</p>
                    <p className="text-sm font-semibold text-gray-800">{preview.calories} kcal</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">蛋白质</p>
                    <p className="text-sm font-semibold text-gray-800">{preview.protein} g</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">碳水</p>
                    <p className="text-sm font-semibold text-gray-800">{preview.carbs} g</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">脂肪</p>
                    <p className="text-sm font-semibold text-gray-800">{preview.fat} g</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">纤维</p>
                    <p className="text-sm font-semibold text-gray-800">{preview.fiber} g</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">维C</p>
                    <p className="text-sm font-semibold text-gray-800">{preview.vitC} mg</p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={saveEdit}
              className="w-full h-12 mt-5 rounded-btn bg-mint-400 text-white text-sm font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line text-lg"></i> 保存修改
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-gray-900/85 text-white text-xs px-4 py-2 rounded-full z-50 whitespace-nowrap">
          {toast}
        </div>
      )}
    </AppShell>
  );
}